import React, { useEffect, useState } from 'react';
import {
  Table,
  Button,
  Space,
  Typography,
  Tag,
  Avatar,
  Badge,
  message,
  Modal,
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  EyeOutlined,
  CarOutlined,
  CalendarOutlined,
  ClockCircleOutlined,
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { axiosInstance, API_ENDPOINTS, listingsService } from '../api';
import { TokenVerificationStatus } from './components/TokenVerificationStatus';

const { Text } = Typography;
const DEFAULT_CAR_IMAGE = '/images/placeholder.jpg';

const UserAdsListing: React.FC = () => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<any[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(5);
  const [tokenVerificationModalVisible, setTokenVerificationModalVisible] = useState(false);
  const [selectedTokenListing, setSelectedTokenListing] = useState<any>(null);

  const fetchListings = async (pageNum = 1, pageSize = limit) => {
    setLoading(true);
    try {
      const { data } = await axiosInstance.get(API_ENDPOINTS.LISTINGS.MY_LISTINGS, { params: { page: pageNum, limit: pageSize } });
      let listings = Array.isArray(data) ? data : (data?.listings || data?.data || []);
      let totalCount = Array.isArray(data) ? data.length : (data?.pagination?.total || data?.total || data?.count || 0);

      const items = listings.map((l: any) => {
        const title = l.title || `${l.year ?? ''} ${l.make ?? ''} ${l.model ?? ''}`.trim();
        const imageUrl = l.thumbnailUrl || l.image || DEFAULT_CAR_IMAGE;
        
        return {
          id: l.id,
          slug: l.slug,
          title,
          make: l.make,
          model: l.model,
          year: l.year,
          price: Number(l.price) || 0,
          mileage: l.mileage || 0,
          location: l.location || l.city || l.country || '',
          status: (l.status || 'DRAFT').toLowerCase(),
          currentStep: l.currentStep,
          stepStatus: l.stepStatus,
          createdAt: l.createdAt,
          views: l.viewCount || 0,
          favorites: l.favoriteCount || 0,
          imageUrl,
        };
      });

      setData(items);
      setTotal(totalCount);
      setPage(pageNum);
      setLimit(pageSize);
    } catch (err) {
      setData([]);
      setTotal(0);
      message.error(t('user_ads.fetch_failed'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchListings(1, limit);
  }, []);

  const navigate = useNavigate();
  const handleCreateAd = () => navigate('/dashboard/add-user-listing');

  const handleEditAd = (record: any) => {
    if (!record?.id) return message.error(t('user_ads.id_not_found'));
    navigate(`/dashboard/add-user-listing?id=${encodeURIComponent(record.id)}`);
  };

  const handleDeleteAd = (record: any) => {
    Modal.confirm({
      title: t('user_ads.delete_title'),
      content: t('user_ads.delete_confirm', { title: record.title }),
      okText: t('user_ads.delete_yes'),
      okType: 'danger',
      cancelText: t('user_ads.delete_cancel'),
      onOk: async () => {
        try {
          await listingsService.delete(record.id);
          message.success(t('user_ads.delete_success'));
          fetchListings(page, limit);
        } catch (err) {
          message.error(t('user_ads.delete_failed'));
        }
      },
    });
  };

  const handleViewAd = (record: any) => {
    const target = record.slug || record.id;
    if (!target) return;
    window.open(`/listing/${target}`, '_blank', 'noopener,noreferrer');
  };

  const handleViewTokenVerification = async (record: any) => {
    try {
      const response = await listingsService.getById(record.id);
      setSelectedTokenListing(response);
    } catch {
      setSelectedTokenListing(record);
    }
    setTokenVerificationModalVisible(true);
  };

  const getStatusTag = (status: string) => {
    const config: Record<string, {color: string, text: string}> = {
      active: { color: 'green', text: t('user_ads.status_active') },
      pending: { color: 'orange', text: t('user_ads.status_pending') },
      sold: { color: 'blue', text: t('user_ads.status_sold') },
      expired: { color: 'red', text: t('user_ads.status_expired') },
      draft: { color: 'default', text: t('user_ads.status_draft') },
      live: { color: 'green', text: t('user_ads.status_live') },
    };
    const mapped = config[status] || { color: 'purple', text: status };
    return <Tag color={mapped.color} style={{ borderRadius: 12, padding: '2px 10px' }}>{mapped.text.toUpperCase()}</Tag>;
  };

  const columns = [
    {
      title: t('user_ads.vehicle_column', 'Vehicle'),
      key: 'vehicle',
      width: 280,
      render: (record: any) => (
        <Space>
          <div style={{ width: 64, height: 48, borderRadius: 8, overflow: 'hidden', background: '#f8f9fa' }}>
            <img
              src={record.imageUrl}
              alt="car"
              style={{ width: '100%', height: '100%', objectFit: 'cover' }}
              onError={(e) => { (e.target as HTMLImageElement).src = '/images/placeholder.jpg'; }}
            />
          </div>
          <div>
            <div className="fw-bold" style={{ fontSize: 14 }}>{record.title}</div>
            <div className="text-muted" style={{ fontSize: 12 }}>{record.year} • {record.mileage.toLocaleString()} km</div>
            <div className="text-muted" style={{ fontSize: 12 }}>{record.location}</div>
          </div>
        </Space>
      ),
    },
    {
      title: t('user_ads.price_column', 'Price'),
      dataIndex: 'price',
      width: 120,
      render: (p: number) => <span className="fw-bold text-dark">{p.toLocaleString()} ALL</span>,
    },
    {
      title: t('user_ads.status_column', 'Status'),
      dataIndex: 'status',
      width: 120,
      render: (status: string) => getStatusTag(status),
    },
    {
      title: 'Metrics',
      key: 'metrics',
      width: 120,
      render: (record: any) => (
        <div>
          <span className="me-3" title="Views"><i className="bi bi-eye text-muted me-1"></i>{record.views}</span>
          <span title="Favorites"><i className="bi bi-heart text-muted me-1"></i>{record.favorites}</span>
        </div>
      ),
    },
    {
      title: t('user_ads.actions_column', 'Actions'),
      key: 'actions',
      width: 140,
      render: (record: any) => (
        <Space>
          <button className="btn btn-sm btn-light text-primary" onClick={() => handleViewAd(record)}><i className="bi bi-eye"></i></button>
          <button className="btn btn-sm btn-light text-primary" onClick={() => handleEditAd(record)}><i className="bi bi-pencil"></i></button>
          <button className="btn btn-sm btn-light text-danger" onClick={() => handleDeleteAd(record)}><i className="bi bi-trash"></i></button>
        </Space>
      ),
    },
  ];

  const stats = {
    totalAds: total,
    activeAds: data.filter(ad => ad.status === 'active' || ad.status === 'live').length,
    totalViews: data.reduce((sum, ad) => sum + (ad.views || 0), 0),
    totalFavorites: data.reduce((sum, ad) => sum + (ad.favorites || 0), 0),
  };

  return (
    <div className="dashboard-ads" style={{ paddingBottom: 40 }}>
      <div className="d-flex justify-content-between align-items-end mb-4">
        <div>
          <h3 className="fw-bold mb-1">{t('user_ads.page_title', 'My Vehicles')}</h3>
          <p className="text-muted mb-0">{t('user_ads.page_subtitle', 'Manage all your listings from here')}</p>
        </div>
        <button className="btn btn-primary shadow-sm" style={{ background: '#fe5a1d', border: 'none' }} onClick={handleCreateAd}>
          <i className="bi bi-plus-circle me-2"></i> {t('user_ads.create_button', 'Create Listing')}
        </button>
      </div>

      <div className="row g-4 mb-4">
        <div className="col-md-3">
          <div className="dash-stat-card shadow-sm" style={{ padding: '20px 24px' }}>
            <div className="dash-stat-icon" style={{ background: 'rgba(254,90,29,0.1)', color: '#fe5a1d', width: 48, height: 48, fontSize: 20 }}>
              <i className="bi bi-car-front"></i>
            </div>
            <div className="dash-stat-info">
              <p className="text-muted mb-0 text-uppercase fw-bold" style={{ fontSize: 10 }}>{t('user_ads.total_ads', 'Total Ads')}</p>
              <h4 className="fw-bold mb-0">{stats.totalAds}</h4>
            </div>
          </div>
        </div>
        <div className="col-md-3">
          <div className="dash-stat-card shadow-sm" style={{ padding: '20px 24px' }}>
            <div className="dash-stat-icon" style={{ background: 'rgba(40,167,69,0.1)', color: '#28a745', width: 48, height: 48, fontSize: 20 }}>
              <i className="bi bi-check-circle"></i>
            </div>
            <div className="dash-stat-info">
              <p className="text-muted mb-0 text-uppercase fw-bold" style={{ fontSize: 10 }}>{t('user_ads.active_ads', 'Active')}</p>
              <h4 className="fw-bold mb-0">{stats.activeAds}</h4>
            </div>
          </div>
        </div>
        <div className="col-md-3">
          <div className="dash-stat-card shadow-sm" style={{ padding: '20px 24px' }}>
            <div className="dash-stat-icon" style={{ background: 'rgba(23,162,184,0.1)', color: '#17a2b8', width: 48, height: 48, fontSize: 20 }}>
              <i className="bi bi-eye"></i>
            </div>
            <div className="dash-stat-info">
              <p className="text-muted mb-0 text-uppercase fw-bold" style={{ fontSize: 10 }}>{t('user_ads.total_views', 'Views')}</p>
              <h4 className="fw-bold mb-0">{stats.totalViews}</h4>
            </div>
          </div>
        </div>
        <div className="col-md-3">
          <div className="dash-stat-card shadow-sm" style={{ padding: '20px 24px' }}>
            <div className="dash-stat-icon" style={{ background: 'rgba(255,193,7,0.1)', color: '#ffc107', width: 48, height: 48, fontSize: 20 }}>
              <i className="bi bi-star"></i>
            </div>
            <div className="dash-stat-info">
              <p className="text-muted mb-0 text-uppercase fw-bold" style={{ fontSize: 10 }}>{t('user_ads.favorites', 'Favorites')}</p>
              <h4 className="fw-bold mb-0">{stats.totalFavorites}</h4>
            </div>
          </div>
        </div>
      </div>

      <div className="dash-panel shadow-sm p-0 overflow-hidden">
        <Table
          columns={columns}
          dataSource={data}
          rowKey="id"
          loading={loading}
          pagination={{
            total, current: page, pageSize: limit,
            showSizeChanger: true, showQuickJumper: true,
            onChange: (pageNum, pageSize) => fetchListings(pageNum, pageSize)
          }}
          scroll={{ x: '100%' }}
          className="ant-table-premium"
        />
      </div>

    </div>
  );
};

export default UserAdsListing;
